import React from 'react';
import { Button, Card, Col, Form, Input, Row } from 'antd';
import DashboardPrimaryActionButton from './dashboard/DashboardPrimaryActionButton';
import RecordAttachments from './RecordAttachments';

function normalizeMetadataValue(value) {
  return value === undefined || value === null || value === '' ? '—' : value;
}

function getMetadataValue(metadata = [], label) {
  return normalizeMetadataValue(metadata.find((item) => item?.label === label)?.value);
}

function ReadOnlyField({ label, value, textarea = false }) {
  return (
    <Form.Item label={label}>
      {textarea ? (
        <Input.TextArea value={normalizeMetadataValue(value)} disabled autoSize={{ minRows: 3 }} />
      ) : (
        <Input value={normalizeMetadataValue(value)} disabled />
      )}
    </Form.Item>
  );
}


export function ExternalSubmittedFormRecord({
  formName,
  recordType,
  recordId,
  metadata,
  performedByLabel = 'Submitted by',
  performedByName,
  notesSignatureTitle = 'Notes & Operator Signature',
  signatureLabel = 'Operator Signature',
  onPrint,
  onCancel,
}) {
  const checklistDetailFields = [
    [`${performedByLabel} / Performed By`, performedByName || getMetadataValue(metadata, 'Operator')],
    ['Customer', getMetadataValue(metadata, 'Customer')],
    ['Unit ID', getMetadataValue(metadata, 'Unit ID')],
    ['Make', getMetadataValue(metadata, 'Make')],
    ['Model', getMetadataValue(metadata, 'Model')],
    ['Serial Number', getMetadataValue(metadata, 'Serial Number')],
    ['Date', getMetadataValue(metadata, 'Date')],
    ['Hour Meter', getMetadataValue(metadata, 'Hour Meter')],
    ['Submitted At', getMetadataValue(metadata, 'Submitted At')],
  ];

  return (
    <div className="external-submitted-record">
      <Form layout="vertical" className="operator-checklist-native-form external-submitted-record-form">
        <Card className="operator-checklist-section-card" title="Checklist Details">
          <Row gutter={[16, 4]}>
            {checklistDetailFields.map(([label, value]) => (
              <Col xs={24} md={12} lg={8} key={label}>
                <ReadOnlyField label={label} value={value} />
              </Col>
            ))}
          </Row>
        </Card>

        <Card className="operator-checklist-section-card" title={notesSignatureTitle}>
          <Row gutter={[16, 4]}>
            <Col xs={24}>
              <ReadOnlyField label="Notes / Summary" value={getMetadataValue(metadata, 'Notes / Summary')} textarea />
            </Col>
            <Col xs={24}>
              <div className="external-submitted-record-signature-row">
                <ReadOnlyField label={signatureLabel} value={getMetadataValue(metadata, signatureLabel)} />
              </div>
            </Col>
          </Row>
        </Card>

        <Card className="operator-checklist-section-card" title="Attachments">
          <div className="external-submitted-record-attachment-groups operator-checklist-readonly-attachments">
            <section className="external-submitted-record-attachment-group" aria-labelledby="external-submitted-form-attachments-heading">
              <h3 id="external-submitted-form-attachments-heading">External Submitted Form</h3>
              <RecordAttachments
                recordType={recordType}
                recordId={recordId}
                readOnly
                attachmentTypes={['EXTERNAL_SUBMITTED_FORM']}
                variant="compactReadOnlySection"
              />
            </section>

            <section className="external-submitted-record-attachment-group" aria-labelledby="optional-photo-attachments-heading">
              <h3 id="optional-photo-attachments-heading">Optional Photos / Attachments</h3>
              <RecordAttachments
                recordType={recordType}
                recordId={recordId}
                readOnly
                attachmentTypes={['PHOTO']}
                variant="compactReadOnlySection"
              />
            </section>
          </div>
        </Card>
      </Form>

      <div className="operator-checklist-readonly-actions external-submitted-record-actions">
        <DashboardPrimaryActionButton onClick={onPrint} className="operator-checklist-readonly-action operator-checklist-readonly-print-action">
          Print
        </DashboardPrimaryActionButton>
        <Button onClick={onCancel} className="dashboard-secondary-action-button operator-checklist-readonly-action">
          Cancel
        </Button>
      </div>
    </div>
  );
}


export function PrintExternalSubmittedFormSummary({ formName, metadata = [], performedByLabel = 'Submitted by', performedByName }) {
  return (
    <div className="external-print-summary">
      <h1>{formName}</h1>
      <h2>External customer form record</h2>
      <p>
        This record was submitted using an external customer form. The native ManageLift form/checklist fields were not completed for this record.
      </p>
      <p>
        Review the authenticated <strong>External submitted form</strong> attachment from the record view to see the completed customer form.
      </p>
      {performedByName && (
        <p><strong>{performedByLabel}:</strong> {performedByName}</p>
      )}
      <table>
        <tbody>
          {metadata.filter((item) => item && item.label).map((item, index) => (
            <tr key={item.key || `${item.label}-${index}`}>
              <th>{item.label}</th>
              <td>{normalizeMetadataValue(item.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="external-print-note">
        Attachment reference: External submitted form (view/download only through ManageLift while authenticated).
      </p>
    </div>
  );
}
